import Cookies from 'js-cookie';
import { isEmpty } from 'radash';
import { match } from 'ts-pattern';
import { create } from 'zustand';
import { authStore } from '@modules/auth/adapter/outbound/stores/auth.store';
import { loadingStore } from '@modules/core/adapter/outbound/stores/ui.store';
import { GetAllFavoriteItemByTypeUsecase } from '@modules/favorite/application/usecases/getAllFavoriteItemByType.usecase';
import { GetFavoriteItemByIdUsecase } from '@modules/favorite/application/usecases/getFavoriteItemById.usecase';
import { UpdateFavoriteItemByIdUsecase } from '@modules/favorite/application/usecases/updateFavoriteItemById.usecase';
import { IFavoriteItem } from '@modules/favorite/domain/models/favourite.model';
import { getAuthCookiesName } from '@shared/helpers/getAuthCookiesName.helper';
import { ModuleType } from '@shared/types/module.type';
import { StatusCodeType } from '@shared/types/statusCode.type';
import { FavioriteRepositoryImpl } from '../../inbound/repositories/faviorite.repository';
interface FavoriteState {
  isFavoriteItem: boolean;
  isLoadingFavorite: boolean;
  updateFavoriteItemById: (id: number, module: ModuleType) => Promise<void>;
  fetchFavoriteItemById: (id: number, module: ModuleType) => Promise<void>;
  resetData: () => void;
  totalProducts: number;
  totalRecipes: number;
  totalCollections: number;
  favoriteProducts: IFavoriteItem[];
  favoriteRecipes: IFavoriteItem[];
  favoriteCollections: IFavoriteItem[];
  fetchAllFavoriteItemByType: (module: ModuleType) => Promise<void>;
}
export const favoriteStateStore = create<FavoriteState>((set, get) => ({
  isFavoriteItem: false,
  isLoadingFavorite: false,
  updateFavoriteItemById: async (id, module) => {
    try {
      const isLoadingFavorite = get().isLoadingFavorite;
      if (isLoadingFavorite) return;
      set(() => ({
        isLoadingFavorite: true
      }));
      loadingStore.setState({
        isLoading: true
      });
      const favioriteRepositoryImpl = new FavioriteRepositoryImpl();
      const updateFavoriteItemByIdUsecase = new UpdateFavoriteItemByIdUsecase(favioriteRepositoryImpl);
      const result = await updateFavoriteItemByIdUsecase.handle(id, module);
      if (result.isRight()) {
        set(() => ({
          isFavoriteItem: result.value
        }));
      }
    } catch (err) {
      console.error('error updateFavoriteItemById', err);
    } finally {
      set(() => ({
        isLoadingFavorite: false
      }));
      loadingStore.setState({
        isLoading: false
      });
    }
  },
  fetchFavoriteItemById: async (id, module) => {
    try {
      const cookies = Cookies.get(getAuthCookiesName());
      if (isEmpty(cookies)) return;
      const isLoadingFavorite = get().isLoadingFavorite;
      if (isLoadingFavorite) return;
      set(() => ({
        isLoadingFavorite: true
      }));
      loadingStore.setState({
        isLoading: true
      });
      const favioriteRepositoryImpl = new FavioriteRepositoryImpl();
      const getRelatedActivityUsecase = new GetFavoriteItemByIdUsecase(favioriteRepositoryImpl);
      const result = await getRelatedActivityUsecase.handle(id, module);
      if (result.isRight()) {
        set(() => ({
          isFavoriteItem: result.value
        }));
      }
    } catch (err) {
      console.error('error fetchFavoriteItemById', err);
    } finally {
      set(() => ({
        isLoadingFavorite: false
      }));
      loadingStore.setState({
        isLoading: false
      });
    }
  },
  resetData: () => {
    set(() => ({
      isFavoriteItem: false,
      isLoadingFavorite: false
    }));
  },
  totalProducts: 0,
  totalRecipes: 0,
  totalCollections: 0,
  favoriteProducts: [],
  favoriteRecipes: [],
  favoriteCollections: [],
  fetchAllFavoriteItemByType: async (module: ModuleType) => {
    try {
      loadingStore.setState({
        isLoading: true
      });
      const favioriteRepositoryImpl = new FavioriteRepositoryImpl();
      const getAllFavoriteItemByTypeUsecase = new GetAllFavoriteItemByTypeUsecase(favioriteRepositoryImpl);
      const result = await getAllFavoriteItemByTypeUsecase.handle(module);
      const {
        totalProducts,
        totalRecipes,
        totalCollections,
        recipes,
        products,
        collections
      } = result;
      match(module).with(ModuleType.Recipe, () => set(() => ({
        totalProducts,
        totalRecipes,
        totalCollections,
        favoriteRecipes: recipes
      }))).with(ModuleType.Product, () => set(() => ({
        totalProducts,
        totalRecipes,
        totalCollections,
        favoriteProducts: products
      }))).with(ModuleType.Collection, () => set(() => ({
        totalProducts,
        totalRecipes,
        totalCollections,
        favoriteCollections: collections
      })));
    } catch (error) {
      console.error('error fetchAllFavoriteItemByType', error);
      if ([StatusCodeType.Unauthorized, StatusCodeType.Forbidden, StatusCodeType.NotFound].includes(error.statusCode)) {
        authStore.getState().logout();
      }
    } finally {
      loadingStore.setState({
        isLoading: false
      });
    }
  }
}));